import { createFileRoute, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Flame } from "lucide-react";

import { Button } from "@/components/ui/button";
import { SignIn } from "@/components/auth/SignIn";
import { supabase } from "@/integrations/supabase/client";

/**
 * Where an assistant is let in.
 *
 * Supabase sends the visitor here to approve or refuse a client that asked to
 * act as them over MCP. Signing in happens on this page rather than through
 * `/auth`: the authorization has to come back to this exact address, and a
 * round trip that lands on the entry screen loses it. That is the whole reason
 * `SignIn` takes a `returnTo` — the block itself is the same one the
 * entry screen and `/auth` show, so every door in works here too.
 */

export const Route = createFileRoute("/.lovable/oauth/consent")({
  // Browser-only: the Supabase client reads its session from local storage,
  // which the server render does not have.
  ssr: false,
  validateSearch: (search: Record<string, unknown>) => ({
    authorization_id:
      typeof search.authorization_id === "string"
        ? search.authorization_id
        : "",
  }),
  beforeLoad: ({ search }) => {
    if (!search.authorization_id) throw new Error("Missing authorization_id");
  },
  loader: async ({ location }) => {
    const authorizationId = new URLSearchParams(location.search).get(
      "authorization_id",
    );
    const { data: session } = await supabase.auth.getSession();
    if (!session.session) return { signedIn: false, details: null };
    const { data, error } = await supabase.auth.oauth.getAuthorizationDetails(
      authorizationId ?? "",
    );
    if (error) throw error;
    // An already-approved client resolves straight away, and comes back as a
    // redirect rather than as something to ask about.
    if (data && "redirect_url" in data)
      throw redirect({ href: data.redirect_url });
    return { signedIn: true, details: data };
  },
  component: Consent,
  errorComponent: LoadFailed,
});

function LoadFailed({ error }: { error: unknown }) {
  const { t } = useTranslation();
  return (
    <Shell>
      <p className="text-sm text-muted-foreground">
        {t("authorize.loadFailed", {
          reason: String(error instanceof Error ? error.message : error),
        })}
      </p>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  return (
    <main className="min-h-svh flex flex-col items-center justify-center px-4 bg-background">
      <div className="w-full max-w-sm space-y-6 text-center">
        <div className="flex items-center justify-center gap-2 text-foreground">
          <Flame className="w-6 h-6 text-primary" aria-hidden="true" />
          <span className="text-lg font-semibold tracking-tight">
            {t("app.name")}
          </span>
        </div>
        {children}
      </div>
    </main>
  );
}

function Consent() {
  const { t } = useTranslation();
  const loaded = Route.useLoaderData();
  const { authorization_id } = Route.useSearch();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!loaded.signedIn) {
    return (
      <Shell>
        <h1 className="text-xl font-semibold">{t("account.signInTitle")}</h1>
        <SignIn
          intent={null}
          returnTo={
            typeof window === "undefined" ? undefined : window.location.href
          }
          reason={t("authorize.signInReason")}
        />
      </Shell>
    );
  }

  const details = loaded.details;
  const name =
    details && "client" in details
      ? (details.client?.name ?? t("authorize.unknownApp"))
      : t("authorize.unknownApp");

  // A failure in an event handler reaches no error boundary, so it is kept in
  // state and shown where the decision was made.
  async function decide(approve: boolean) {
    setBusy(true);
    const { data, error: failure } = approve
      ? await supabase.auth.oauth.approveAuthorization(authorization_id)
      : await supabase.auth.oauth.denyAuthorization(authorization_id);
    if (failure) {
      setBusy(false);
      setError(failure.message);
      return;
    }
    const target = data?.redirect_url;
    if (!target) {
      setBusy(false);
      setError(t("authorize.noReturn"));
      return;
    }
    window.location.href = target;
  }

  return (
    <Shell>
      <h1 className="text-xl font-semibold">
        {t("authorize.title", { name })}
      </h1>
      <p className="text-sm text-muted-foreground">
        {t("authorize.body", { name })}
      </p>
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
      <div className="flex gap-3">
        <Button
          size="lg"
          variant="secondary"
          disabled={busy}
          onClick={() => decide(false)}
          className="flex-1"
        >
          {t("authorize.deny")}
        </Button>
        <Button
          size="lg"
          disabled={busy}
          onClick={() => decide(true)}
          className="flex-1"
        >
          {t("authorize.approve")}
        </Button>
      </div>
    </Shell>
  );
}
